import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Requests from "./pages/Requests";
import Suppliers from "./pages/Suppliers";
import Fleet from "./pages/Fleet";
import Planner from "./pages/Planner";
import Calendar from "./pages/Calendar";
import DriverView from "./pages/DriverView";
import UsersPage from "./pages/Users";
import Settings from "./pages/Settings";
import ComingSoon from "./pages/ComingSoon";
import ProductionOrders from "./pages/ProductionOrders";
import { Scissors, Ruler, Layers, Sofa, Wrench, Warehouse } from "lucide-react";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-lm-cream">
      <div className="text-center">
        <img src="/logo-mascheroni.webp" alt="LM" className="h-10 mx-auto mb-4 animate-pulse" />
        <p className="text-lm-muted text-sm">Caricamento…</p>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (user && user.role === "autista") nav("/autista", { replace: true });
  }, [user, nav]);
  if (user?.role === "autista") return null;
  return <Dashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<RoleHome />} />
        <Route path="/richieste" element={<Requests />} />
        <Route path="/pianificazione" element={<Planner />} />
        <Route path="/calendario" element={<Calendar />} />
        <Route path="/anagrafica" element={<Suppliers />} />
        <Route path="/flotta" element={<Fleet />} />
        <Route path="/produzione/taglio" element={<RequireRole roles={["amministratore", "pm_produzione"]}><ProductionOrders department="taglio" title="Taglio" subtitle="Commesse di taglio e stato di lavorazione" icon={Scissors} /></RequireRole>} />
        <Route path="/produzione/cucito" element={<RequireRole roles={["amministratore", "pm_produzione"]}><ProductionOrders department="cucito" title="Cucito" subtitle="Commesse di cucito e stato di lavorazione" icon={Ruler} /></RequireRole>} />
        <Route path="/produzione/rivestimento" element={<RequireRole roles={["amministratore", "pm_produzione"]}><ProductionOrders department="rivestimento" title="Rivestimento" subtitle="Commesse di rivestimento e stato di lavorazione" icon={Layers} /></RequireRole>} />
        <Route path="/produzione/tappezzeria" element={<RequireRole roles={["amministratore", "pm_produzione"]}><ProductionOrders department="tappezzeria" title="Tappezzeria" subtitle="Commesse di tappezzeria e stato di lavorazione" icon={Sofa} /></RequireRole>} />
        <Route path="/produzione/montaggio" element={<RequireRole roles={["amministratore", "pm_produzione"]}><ProductionOrders department="montaggio" title="Montaggio" subtitle="Commesse di montaggio e stato di lavorazione" icon={Wrench} /></RequireRole>} />
        <Route path="/magazzino" element={<RequireRole roles={["amministratore", "pm_produzione"]}><ComingSoon title="Magazzino" icon={Warehouse} description="Giacenze, entrate e uscite, ubicazioni di materiali e prodotti finiti." /></RequireRole>} />
        <Route path="/autista" element={<DriverView />} />
        <Route path="/utenti" element={<UsersPage />} />
        <Route path="/impostazioni" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}
